import { useState, type FormEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiRequestError } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { copy, useI18n } from '@/lib/i18n';
import { Button, Card, Field, Input, PageHeader, Select, Textarea } from '@/components/ui';
import './CustomerPages.css';

interface QuoteResult { id: string }

const SOLUTIONS = ['Leds Play', 'VR Deneyimleri', 'Etkileşimli Dijital Sanat', 'AR Deneyimleri', 'Özel deneyim projesi'];

export default function QuoteRequest() {
  const me = useMe();
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const [params] = useSearchParams();
  const location = useLocation();
  const initialSolution = params.get('cozum')?.trim() || params.get('katalog')?.trim() || SOLUTIONS[0]!;
  const [form, setForm] = useState({
    organization: me.account.name,
    contactName: me.user.name,
    email: me.user.email,
    phone: '',
    city: '',
    solution: initialSolution,
    message: '',
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [createdId, setCreatedId] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post<QuoteResult>('/quotes', {
      ...form,
      phone: form.phone.trim() || null,
      city: form.city.trim() || null,
      sourceCatalog: params.get('katalog')?.trim() || null,
      sourcePath: params.get('kaynak')?.startsWith('/') ? params.get('kaynak') : location.pathname,
    }),
    onSuccess: (result) => setCreatedId(result.id),
    onError: (error) => setFields(error instanceof ApiRequestError ? error.fields ?? { form: error.message } : { form: c('Talep gönderilemedi.', 'The request could not be sent.') }),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setFields({});
    create.mutate();
  }

  if (createdId) {
    return (
      <div className="quote-page">
        <Card className="quote-success">
          <span className="quote-success__mark" aria-hidden="true">✓</span>
          <p className="quote-page__eyebrow">{c('TALEP ALINDI', 'REQUEST RECEIVED')}</p>
          <h1>{c('İhtiyacınızı birlikte netleştireceğiz.', 'We’ll clarify your needs together.')}</h1>
          <p>{c('Wondeon ekibi talebinizi inceleyip kurumunuzla iletişime geçecek. Talep numaranızı gerektiğinde paylaşabilirsiniz.', 'The Wondeon team will review your request and contact your organization. You can share the request number when needed.')}</p>
          <code>{createdId}</code>
          <Link className="btn btn--primary btn--lg" to="/kesfet">{c('Keşfet’e dön', 'Return to Discover')}</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="quote-page stack stack--lg">
      <PageHeader title={c('Teklif Al', 'Request a Quote')} description={c('İhtiyacınızı anlatın; ekip, kapsamı ve uygun teslim modelini sizinle birlikte netleştirsin.', 'Tell us what you need; our team will clarify the scope and delivery model with you.')} />
      <div className="quote-layout">
        <aside className="quote-context">
          <p className="quote-page__eyebrow">{c('KURUMSAL PROJE', 'BUSINESS PROJECT')}</p>
          <h2>{form.solution}</h2>
          <p>{c('Bu form fiyat yayımlamaz. Talebiniz kurum, alan, etkinlik ve teknik ihtiyaç bağlamında değerlendirilir.', 'This form does not publish pricing. Your request is assessed in the context of your organization, venue, event and technical needs.')}</p>
          <ul>
            <li>{c('Tek temas noktası', 'A single point of contact')}</li>
            <li>{c('İhtiyaca göre teknik kapsam', 'A needs-based technical scope')}</li>
            <li>{c('Hub üzerinden izlenebilir süreç', 'A process traceable through Hub')}</li>
          </ul>
        </aside>
        <Card form className="quote-form-card">
          <form className="quote-form" onSubmit={submit}>
            <div className="quote-form__two">
              <Field label={c('Kurum adı', 'Organization')} required error={fields.organization}>{({ id, describedBy, invalid }) => <Input id={id} required minLength={2} value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} aria-describedby={describedBy} invalid={invalid} />}</Field>
              <Field label={c('Yetkili kişi', 'Contact person')} required error={fields.contactName}>{({ id, describedBy, invalid }) => <Input id={id} required minLength={2} value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} aria-describedby={describedBy} invalid={invalid} />}</Field>
            </div>
            <div className="quote-form__two">
              <Field label={c('Kurumsal e-posta', 'Business email')} required error={fields.email}>{({ id, describedBy, invalid }) => <Input id={id} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-describedby={describedBy} invalid={invalid} />}</Field>
              <Field label={c('Telefon', 'Phone')} error={fields.phone}>{({ id, describedBy, invalid }) => <Input id={id} type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} aria-describedby={describedBy} invalid={invalid} />}</Field>
            </div>
            <div className="quote-form__two">
              <Field label={c('Çözüm alanı', 'Solution area')} required error={fields.solution}>{({ id, describedBy, invalid }) => <Select id={id} required value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} aria-describedby={describedBy} aria-invalid={invalid || undefined}>{Array.from(new Set([initialSolution, ...SOLUTIONS])).map((item) => <option key={item} value={item}>{item}</option>)}</Select>}</Field>
              <Field label={c('Şehir', 'City')} error={fields.city}>{({ id, describedBy, invalid }) => <Input id={id} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} aria-describedby={describedBy} invalid={invalid} />}</Field>
            </div>
            <Field label={c('Proje ihtiyacı', 'Project need')} required error={fields.message} hint={c('Alan, hedef kitle, etkinlik tarihi ve varsa mevcut teknik altyapıyı yazabilirsiniz.', 'You can include the venue, audience, event date and current technical setup.')}>{({ id, describedBy, invalid }) => <Textarea id={id} required minLength={10} rows={7} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} aria-describedby={describedBy} aria-invalid={invalid || undefined} />}</Field>
            {fields.form && <p className="field__error" role="alert">{fields.form}</p>}
            <div className="quote-form__footer">
              <p>{c('Hesabınızı oluştururken kabul ettiğiniz', 'Your accepted')} <Link to="/kvkk-aydinlatma" target="_blank">{c('KVKK Aydınlatma Metni', 'Privacy Notice')}</Link> {c('ve', 'and')} <Link to="/kullanim-kosullari" target="_blank">{c('Kullanım Koşulları', 'Terms of Use')}</Link> {c('bu talep için de geçerlidir.', 'also apply to this request.')}</p>
              <Button type="submit" variant="primary" size="lg" loading={create.isPending}>{c('Talebi gönder', 'Send request')}</Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
